import React, { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import {
  MultiSelectHeader,
  SelectAllToggle,
  SelectedCount,
} from 'controlled-components'
import {
  deselectAllBottomOnPage,
  filterOptionsBySearch,
  selectAllBottomOnPage,
  selectBottomData,
  selectBottomSearchTerm,
  selectBottomSelectedItems,
  selectBottomSelectedItemsCount,
  selectBottomShowingSelections,
  toggleBottomShowingSelections,
} from 'slices/aiSegmentBuilder/picker'

import { PaginationContainer } from './Header'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
  goToPage?: (page: number) => void
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the dropdown header.
 * Handles select all toggle, selection count, and pagination controls.
 */
const HeaderContainer: React.FC<Props> = ({
  pickerId,
  goToPage,
  data,
  searchTerm,
  selectedItems,
  selectionCount,
  showingSelections,
  selectAllOnPage,
  deselectAllOnPage,
  toggleShowingSelections,
}) => {
  // Derived: selected items set for checking page selection state
  const selectedItemsSet = useMemo(
    () => new Set(selectedItems),
    [selectedItems]
  )

  // Derived: the visible "page" — filtered by the active search term via the
  // same shared helper the options list uses, so "select all on page" only
  // ever targets the options the user can actually see.
  const currentPageKeys = useMemo(
    () => filterOptionsBySearch(data, searchTerm).map(item => item.id),
    [data, searchTerm]
  )

  const allPageSelected = useMemo(() => {
    if (currentPageKeys.length === 0) return false
    return currentPageKeys.every(key => selectedItemsSet.has(key))
  }, [currentPageKeys, selectedItemsSet])

  const somePageSelected = useMemo(() => {
    if (currentPageKeys.length === 0) return false
    const selectedOnPage = currentPageKeys.filter(key =>
      selectedItemsSet.has(key)
    )
    return (
      selectedOnPage.length > 0 &&
      selectedOnPage.length < currentPageKeys.length
    )
  }, [currentPageKeys, selectedItemsSet])

  // Handlers
  const handleSelectAllToggle = () => {
    if (allPageSelected) {
      deselectAllOnPage({ pickerId, keys: currentPageKeys })
    } else {
      selectAllOnPage({ pickerId, keys: currentPageKeys })
    }
  }

  const handleSelectedClick = () => {
    toggleShowingSelections({ pickerId })
  }

  return (
    <MultiSelectHeader>
      <MultiSelectHeader.Left>
        {!showingSelections && (
          <SelectAllToggle
            checked={allPageSelected}
            indeterminate={somePageSelected}
            onToggle={handleSelectAllToggle}
          />
        )}
        <SelectedCount
          count={selectionCount}
          onClick={handleSelectedClick}
          active={showingSelections}
        />
      </MultiSelectHeader.Left>
      {!showingSelections && (
        <MultiSelectHeader.Right>
          <PaginationContainer pickerId={pickerId} goToPage={goToPage} />
        </MultiSelectHeader.Right>
      )}
    </MultiSelectHeader>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  data: selectBottomData(state, ownProps.pickerId),
  searchTerm: selectBottomSearchTerm(state, ownProps.pickerId),
  selectedItems: selectBottomSelectedItems(state, ownProps.pickerId),
  selectionCount: selectBottomSelectedItemsCount(state, ownProps.pickerId),
  showingSelections: selectBottomShowingSelections(state, ownProps.pickerId),
})

const mapDispatchToProps = {
  selectAllOnPage: selectAllBottomOnPage,
  deselectAllOnPage: deselectAllBottomOnPage,
  toggleShowingSelections: toggleBottomShowingSelections,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

const ConnectedHeaderContainer = connector(HeaderContainer)

// Add displayName so MultiSelectMenu can identify this as the header
ConnectedHeaderContainer.displayName = 'MultiSelectHeader'

export default ConnectedHeaderContainer
